import { defineStore, type StateTree } from 'pinia';
import {
  type DeliveryDestination,
  type DeliveryUserDefined,
  type RecordEntityId,
  type DeliveryDestinationDelete,
  type DeliveryDestinationRequest,
  PermissionScope,
  P2Role,
} from '@centric-os/types';
import { isNil } from 'lodash-es';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';

/**
 * Interface representing the state of the DropOff store.
 * @typedef {Object} State
 * @property {boolean} showCreateLocation - Indicates if the create location form is shown.
 * @property {string | null} currentTimeslotEdit - The current timeslot being edited.
 * @property {string[]} initialValuesForAutomaticTimeslot - Initial values for automatic timeslots.
 * @property {DeliveryUserDefined | null} initialValuesCustomTimeslot - Initial values for custom timeslots.
 * @property {number | null} currentCustomTimeslotIndex - The index of the current custom timeslot being edited.
 * @property {DeliveryDestination | null} deliveryDestination - The currently selected delivery destination.
 */
interface State extends StateTree {
  showCreateLocation: boolean;
  showEditLocation: boolean;
  editDeliveryDestination: DeliveryDestination | null;
  currentTimeslotEdit: string | null;
  initialValuesForAutomaticTimeslot: string[];
  initialValuesCustomTimeslot: DeliveryUserDefined | null;
  currentCustomTimeslotIndex: number | null;
  deletedLocationName: string | null;
  deliveryDestination: DeliveryDestination | null;
}

/**
 * Creates a store for managing drop-off locations.
 * @param {string} [storeId='dropOff'] - The unique ID of the store.
 * @returns {import('pinia').Store} The Pinia store instance.
 */
export function useDropOffStore(storeId = 'dropOff') {
  const store = defineStore(storeId, {
    state: (): State => ({
      showCreateLocation: false,
      showEditLocation: false,
      editDeliveryDestination: null,
      currentTimeslotEdit: null,
      initialValuesForAutomaticTimeslot: [],
      initialValuesCustomTimeslot: null,
      currentCustomTimeslotIndex: null,
      deletedLocationName: null,
      deliveryDestination: null,
    }),
    actions: {
      /**
       * Creates a new drop-off location.
       * @param {Partial<DeliveryDestination>} payload - The payload containing drop-off location details.
       * @param {RecordEntityId} siteId - The ID of the site where the drop-off location is being created.
       * @returns {Promise<void>} A promise that resolves when the drop-off location is created.
       */
      async createDropOffLocation(
        payload: Partial<DeliveryDestination>,
        siteId: RecordEntityId,
      ): Promise<void> {
        const response = await this.serviceApi.post_location_group_deliverydestination(
          String(siteId),
          // @ts-expect-error FIXME: hand-maintained DeliveryDestination vs SDK body (TS2345)
          { ...payload },
        );

        // @ts-expect-error FIXME: SDK DeliveryDestination vs hand-maintained one (TS2322)
        this.deliveryDestination = response;
      },

      /**
       * Updates a existing drop-off location.
       * @param {Partial<DeliveryDestination>} payload - The payload containing drop-off location details.
       * @param {RecordEntityId} siteId - The ID of the site where the drop-off location is being updated.
       * @returns {Promise<void>} A promise that resolves when the drop-off location is updated.
       */
      async updateDropOffLocation(
        payload: Partial<DeliveryDestination>,
        siteId: RecordEntityId,
      ): Promise<void> {
        // Map the payload to the correct format
        const mappedPayload: DeliveryDestinationRequest = {
          delivery_destinations: [payload as DeliveryDestination],
        };

        const response = await this.serviceApi.patch_location_group_deliverydestination(
          String(siteId),
          // @ts-expect-error FIXME: hand-maintained DeliveryDestination vs SDK body (TS2345)
          mappedPayload,
        );

        // We are only updating one delivery destination at a time
        // @ts-expect-error FIXME: SDK DeliveryDestination vs hand-maintained one (TS2322)
        this.deliveryDestination = response.delivery_destinations[0];
      },

      /**
       * Updates existing drop-off locations.
       * @param {DeliveryDestination[]} payload - The payload containing drop-off locations.
       * @param {RecordEntityId} siteId - The ID of the site where the drop-off location is being updated.
       * @returns {Promise<void>} A promise that resolves when the drop-off locations are updated.
       */
      async updateDropOffLocationBulk(
        payload: DeliveryDestination[],
        siteId: RecordEntityId,
      ): Promise<void> {
        // Map the payload to the correct format
        const mappedPayload: DeliveryDestinationRequest = {
          delivery_destinations: payload,
        };

        await this.serviceApi.patch_location_group_deliverydestination(
          String(siteId),
          // @ts-expect-error FIXME: hand-maintained DeliveryDestination vs SDK body (TS2345)
          mappedPayload,
        );
      },

      /**
       * Deletes an existing drop-off location.
       * @param {DeliveryDestinationDelete} payload - The payload containing drop-off location delete details.
       * @param {RecordEntityId} siteId - The ID of the site where the drop-off location is being deleted.
       * @returns {Promise<void>} A promise that resolves when the drop-off location is deleted.
       */
      async deleteDropOffLocation(
        payload: DeliveryDestinationDelete,
        siteId: RecordEntityId,
      ): Promise<void> {
        if (!isNil(payload) && !isNil(siteId)) {
          await this.serviceApi.delete_location_group_deliverydestination(
            String(siteId),
            // @ts-expect-error FIXME: RecordEntityId[] vs SDK string[] ids (TS2345)
            payload,
          );
        } else {
          return Promise.reject(new Error('no location and/or site selected'));
        }
      },
    },
    getters: {
      /**
       * Retrieves if logged user can manage drop-off locations.
       * @returns {Boolean} True/false if user can.
       */
      canManageDropOffLocations(_state: State): boolean {
        const authStore = useAuthStore();
        return (
          authStore.hasRole(P2Role.ADMIN) ||
          authStore.hasPermissions([PermissionScope.canManageDropoffLocations])
        );
      },
    },
    pagination: false,
  });
  return store();
}
